import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./Transactions.css";
import { useAuth } from "../../Auth/AuthContext";
import config from "../../config";
// import GenerateBill from './GenerateBillModal';
import * as XLSX from 'xlsx'; // You'll need to install this package with: npm install xlsx
const listTransactionsUrl = `${config.BASE_URL}/list-all-transactions`;
const updateTransactionsUrl = `${config.BASE_URL}/update-transactions`;
const generatePDFTransactionUrl = `${config.BASE_URL}/generate-pdf-transaction`;
const generatePdfBillIdUrl = `${config.BASE_URL}/generate-pdf-by-bill-id-transaction`;
const deleteTransactionUrl = `${config.BASE_URL}/delete-transaction`;

const Transactions = () => {

  const { setActiveModule } = useAuth();

  const [selectedVendor, setSelectedVendor] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]); // New state for filtered transactions
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [noDataMessage, setNoDataMessage] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [editMode, setEditMode] = useState(false);
  const editRef = useRef(null);
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const vendorSearchRef = useRef(null);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [showSelectAll, setShowSelectAll] = useState(false);
  const [showBillModal, setShowBillModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    recordId: null,
  });


  useEffect(() => {
    sessionStorage.setItem('actMod', 'transactions');
    setActiveModule(sessionStorage.getItem('actMod'));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.d3')) {
        setEditingId(null);
        setEditingData({})
        setEditMode(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };

  }, [editMode]);

  useEffect(() => {
    const vendors = [...new Set(transactions.map(t => t.vendor))];
    setVendorSuggestions(vendors);
  }, [transactions]);

  // Filter transactions when selectedVendor changes
  useEffect(() => {
    if (selectedVendor) {
      const filtered = transactions.filter(t =>
        t.vendor?.toLowerCase().includes(selectedVendor.toLowerCase())
      );
      setFilteredTransactions(filtered);
    } else {
      setFilteredTransactions(transactions);
    }
  }, [selectedVendor, transactions]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (vendorSearchRef.current && !vendorSearchRef.current.contains(event.target)) {
        setShowVendorDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleVendorSelect = (vendor) => {
    setSelectedVendor(vendor);
    setShowVendorDropdown(false);
  };

  const fetchTransactions = async (start = "", end = "") => {
    setErrorMessage("");
    setNoDataMessage("");
    setTransactions([]);

    try {
      const response = await axios.post(listTransactionsUrl, { startDate: start, endDate: end }, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        }
      });
      console.log("response::>>", response.data)
      if (response?.data && Array.isArray(response.data)) {
        setTransactions(response.data);
        setFilteredTransactions(response.data);
      } else if (response?.data?.message) {
        setNoDataMessage(response.data.message);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setNoDataMessage("No data found for this date range.");
    }
  };

  const handleSearch = (e) => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates.");
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      alert("End date should be greater than start date.");
      return;
    } else {
      fetchTransactions(startDate, endDate);
    }
  };

  const groupByDate = () => {
    const transactionsToGroup = selectedVendor ? filteredTransactions : transactions;
    return transactionsToGroup.reduce((groups, transaction) => {
      const dateKey = normalizeDateForKey(transaction.date);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(transaction);
      return groups;
    }, {});
  };

  // Then create the grouped transactions
  const groupedTransactions = groupByDate();

  function formatToReadableDate(dateStr) {
    const [yyyy, mm, dd] = ""; // from normalized key
    const date = new Date(yyyy, mm - 1, dd);

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    function getDayWithSuffix(day) {
      if (day > 3 && day < 21) return `${day}th`;
      switch (day % 10) {
        case 1: return `${day}st`;
        case 2: return `${day}nd`;
        case 3: return `${day}rd`;
        default: return `${day}th`;
      }
    }

    return `${getDayWithSuffix(parseInt(dd))} ${monthNames[parseInt(mm) - 1]}, ${yyyy}`;
  }

  const capitalizeWords = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  function normalizeDateForKey(dateStr) {
    const [dd, mm, yyyy] = ""; //dateStr.split('/');
    return `${yyyy}-${mm}-${dd}`; // e.g., "2025-04-14" (sortable & valid)
  }

  const handleEdit = (id) => {
    const transaction = transactions.find((item) => item._id === id);
    setEditingId(id);
    setEditingData({ ...transaction });
    setEditMode(true);
  };

  const handleEditingChange = (e) => {
    const { name, value } = e.target;
    setEditingData({ ...editingData, [name]: value });
  };

  const handleSave = async (id) => {
    try {
      const payload = {
        updateRows: editingData,
      };
      const response = await axios.post(updateTransactionsUrl, payload,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          }
        }
      );
      console.log("response::>>", response.data.message)
      if (response.status === 201) {
        setTransactions((prev) =>
          prev.map((item) =>
            item._id === id ? { ...editingData } : item
          )
        );
        setEditingId(null);
        setEditingData({});
        alert(response.data.message);
        fetchTransactions();
      }
    } catch (error) {
      console.error("Error updating row:", error);
      alert("Failed to update row.");
    }
  }

  const handleGenerateBillClick = () => {
    if (selectedTransactions.length === 0) {
      alert("Please select at least one row to generate the bill.");
      return;
    }
    setShowBillModal(true);
  };

  const handleGenerateBill = async (additionalRemarks, applyTaxes) => {
    try {

      const vendorSet = new Set();
      selectedTransactions.forEach(id => {
        const transaction = transactions.find(t => t._id === id);
        if (transaction?.vendor) {
          vendorSet.add(transaction.vendor);
        }
      });
      console.log("vendorSet:::>>", vendorSet);

      if (vendorSet.size > 1) {
        alert("Please select transactions from only one vendor to generate the bill.");
        setShowBillModal(false);
        return;
      }

      const payload = selectedTransactions.map(id => {
        const transaction = transactions.find(t => t._id === id);
        return {
          id,
          vendor: transaction?.vendor,
          transaction_status: transaction?.transaction_status,
          destfrom: transaction?.destination_from,
          dest: transaction?.destination_to,
          wt: transaction?.weight,
          actwt: transaction?.actual_weight,
          frt: transaction?.freight,
          rate: transaction?.rate,
          dsl: transaction?.diesel,
          adv: transaction?.advance,
          toll: transaction?.toll,
          tds: transaction?.tds,
          billid: transaction?.billing_id,
          remarks: additionalRemarks,
          applyTaxes: applyTaxes,
        };
      });

      const response = await axios.post(
        generatePDFTransactionUrl,
        payload,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
          responseType: "blob"
        }
      );

      if (response.headers['content-type']?.includes('application/json')) {
        const errorData = JSON.parse(await new Response(response.data).json());
        if (errorData.errorCode === 'MULTIPLE_VENDORS') {
          throw new Error('MULTIPLE_VENDORS_ERROR');
        }
        throw new Error(errorData.message || "Failed to generate bill");
      }

      const contentDisposition = response.headers['content-disposition'];
      let filename = "GeneratedTransactionBill.pdf"; // Fallback name

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1]; // Use exactly what backend sent
        }
      }
      console.log("filename::>>", filename)
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      alert("Bill generated successfully with remarks.");
      setSelectedTransactions([]);
      setShowSelectAll(false);
      fetchTransactions();
      setShowBillModal(false);
    } catch (error) {
      console.error("Error generating bill:", error);
      // Handle specific error cases
      if (error.response && error.response.data instanceof Blob) {
        try {
          const errorData = JSON.parse(await error.response.data.text());
          if (errorData.errorCode === "MULTIPLE_VENDORS") {
            alert("Error: Please select transactions from only one vendor.");
            return;
          }
          alert(`Error: ${errorData.message || "Failed to generate bill"}`);
        } catch (parseError) {
          console.error("Failed to parse error blob:", parseError);
          alert("Failed to generate bill. Please try again.");
        }
        return;
      }

      // Handle other error types
      if (error.message === "MULTIPLE_VENDORS") {
        alert("Error: Please select transactions from only one vendor.");
      } else {
        alert(`Error: ${error.message || "Failed to generate bill"}`);
      }
    }
  };

  const generatePDFTransactionByBillId = async (billId) => {
    try {
      const response = await axios.get(
        `${generatePdfBillIdUrl}/${billId}`,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
          responseType: "blob"
        }

      );
      console.log("response::>>", response.data.message)
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `GeneratedTransactionBill-${billId}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      alert("Transaction bill downloaded successfully.");
    } catch (error) {
      console.error("Error generating bill:", error);
      alert("Failed to generate bill.");
    }
  };

  const handleSingleSelect = (transactionId, isChecked) => {
    if (isChecked) {
      setSelectedTransactions(prev => [...prev, transactionId]);
      setShowSelectAll(true);
    } else {
      setSelectedTransactions(prev => prev.filter(id => id !== transactionId));
      if (selectedTransactions.length === 1) {
        setShowSelectAll(false);
      }
    }
  };

  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      const allIds = Object.values(groupedTransactions)
        .flat()
        .map(transaction => transaction._id);
      setSelectedTransactions(allIds);
    } else {
      setSelectedTransactions([]);
      setShowSelectAll(false);
    }
  };

  const downloadExcel = () => {
    // Get the transactions to export (filtered or all)
    const transactionsToExport = selectedVendor ? filteredTransactions : transactions;

    if (transactionsToExport.length === 0) {
      alert("No data available to export.");
      return;
    }

    // Create data for Excel
    const exportData = transactionsToExport.map(transaction => ({
      "Bill ID": transaction.billing_id,
      "Vendor": transaction.vendor,
      "Wt.": transaction.weight,
      "A. Wt.": transaction.actual_weight,
      "Freight": transaction.freight,
      "Rate": transaction.rate,
      "Diesel": transaction.diesel,
      "Adv.": transaction.advance,
      "Toll": transaction.toll,
      "TDS": transaction.tds,
      "Short": transaction.short,
      "Amount": transaction.amount
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths (characters)
    const columnWidths = [
      { wch: 15 },    // Bill ID
      { wch: 30 },    // Vendor (considerably wider)
      { wch: 12 },    // Weight
      { wch: 15 },    // Actual Weight
      { wch: 12 },    // Freight
      { wch: 12 },    // Rate
      { wch: 12 },    // Diesel
      { wch: 12 },    // Advance
      { wch: 12 },    // Toll
      { wch: 12 },    // TDS
      { wch: 12 },    // Short
      { wch: 15 }     // Amount
    ];

    // Apply column widths
    ws['!cols'] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Transactions");

    // Generate filename with current date
    const now = new Date();
    // const dateStr = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}`;
    const fileName = `Transactions.xlsx`;

    // Write to file and trigger download
    XLSX.writeFile(wb, fileName);
  };
  const handleDeleteTransaction = async (id) => {
    const transaction = transactions.find(t => t._id === id);
    console.log("transaction::>>", transaction)
    if (transaction) {
      setDeleteModal({
        isOpen: true,
        recordId: id,
        billId: transaction.billing_id,
      });
    }
  };

  const confirmDelete = async () => {
    // if (window.confirm("Are you sure you want to delete this transaction?")) {
      try {
        const response = await axios.post(deleteTransactionUrl, { billId: deleteModal.billId }, {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          }
        });
        if (response.status === 200) {
          fetchTransactions();
          alert("Transaction deleted successfully.");
          // setTransactions((prev) => prev.filter((item) => item._id !== id));
        } else {
          alert("Failed to delete transaction.");
        }
        setDeleteModal({ isOpen: false, recordId: null, billId: null });
      } catch (error) {
        console.error("Error deleting transaction:", error);
        alert("Failed to delete transaction.");
      }
    // }
  };

    useEffect(() => {
      fetchTransactions();
    }, []);

    return (
      <div id="transactions">
        <div className="trans-wrapper">
          <div className="form-group">
            <div className="filterItem">
              <label htmlFor="start-date">Start Date:</label>
              <input
                type="date"
                id="start-date"
                name="start-date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>
            <div className="filterItem">
              <label htmlFor="end-date">End Date:</label>
              <input
                type="date"
                id="end-date"
                name="end-date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
              />
            </div>
          </div>
          <button type="button" className="search-button" onClick={handleSearch}>
            Search
          </button>

          {/* Download Excel Button */}
          <button
            className="download-excel-button"
            onClick={downloadExcel}
          >
            Download Excel
          </button>

        </div>
        {errorMessage && <div id="error-message" className="error-message">{errorMessage}
        </div>
        }

        <div className="transaction-ack">
          {/* {noDataMessage && <div className="no-data-message">{noDataMessage}</div>} */}

          <div className="table-wrapper">
            <div className="table-controls">
              {/* {showBillModal && (
              <GenerateBill
                selectedTransactions={selectedTransactions}
                transactions={transactions}
                onClose={() => setShowBillModal(false)}
                onGenerate={handleGenerateBill}
              />
            )} */}

              {/* Vendor Search Component */}
              <div className="vendor-search-container" ref={vendorSearchRef}>
                <input
                  type="text"
                  className="vendor-filter"
                  placeholder="Search Vendor..."
                  value={selectedVendor}
                  onChange={(e) => {
                    const input = e.target.value;
                    setSelectedVendor(input);
                    setShowVendorDropdown(input.length > 0);
                  }}
                  onFocus={() => setShowVendorDropdown(true)}
                />

                {showVendorDropdown && (
                  <div className="vendor-dropdown">
                    {vendorSuggestions
                      .filter(vendor =>
                        vendor.toLowerCase().includes(selectedVendor.toLowerCase())
                      )
                      .length > 0 ? (
                      vendorSuggestions
                        .filter(vendor =>
                          vendor.toLowerCase().includes(selectedVendor.toLowerCase())
                        )
                        .map((vendor, index) => (
                          <div
                            key={index}
                            className="dropdown-item"
                            onClick={() => handleVendorSelect(vendor)}
                          >
                            {vendor}
                          </div>
                        ))
                    ) : (
                      <div className="dropdown-no-results">
                        No vendors found
                      </div>
                    )}
                  </div>
                )}
              </div>

              {groupedTransactions && Object.keys(groupedTransactions).length > 0 ? (
                Object.entries(groupedTransactions).map(([dateKey, trucks], index) => (
                  <div key={index} className="date-section">
                    {/* <div className="date-header">{formatToReadableDate(dateKey)}</div> */}
                    <div className="scrollable-table">
                      <table id="resultsTable">
                        <thead>
                          <tr>
                            <th>Bill ID</th>
                            <th>Vendor</th>
                            <th>Wt.</th>
                            <th>A. Wt.</th>
                            <th>Freight</th>
                            <th>Rate</th>
                            <th>(-)Diesel</th>
                            <th>(-)Adv.</th>
                            <th>(-)Toll</th>
                            <th>(-)TDS</th>
                            <th>(-)Short</th>
                            <th>(-)Remarks</th>
                            <th>(+)GST Amount</th>
                            <th>Amount</th>
                            <th>Delete</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trucks.map((transaction, idx) => (
                            <tr key={transaction._id} className={transaction._id === "totals" ? "totals-row" : ""}>
                              <td>
                                {transaction._id !== "totals" && (
                                  <span className="download-icon" onClick={() => generatePDFTransactionByBillId(transaction.billing_id)}>
                                    <i className="fas fa-download"></i>
                                  </span>
                                )}
                                {transaction.billing_id}
                              </td>
                              <td>{transaction.vendor}</td>
                              <td>{transaction.weight}</td>
                              <td>{transaction.actual_weight}</td>
                              <td>{transaction.freight}</td>
                              <td>{transaction.rate}</td>
                              <td>{transaction.diesel}</td>
                              <td>{transaction.advance}</td>
                              <td>{transaction.toll}</td>
                              <td>{transaction.tds}</td>
                              <td>{transaction.short}</td>
                              <td>{transaction.remarks}</td>
                              <td>{transaction.gst}</td>
                              <td>{transaction.amount}</td>
                              <td>
                                <button
                                  className="delete-btn"
                                  onClick={() => handleDeleteTransaction(transaction._id)}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-results-message">
                  {selectedVendor
                    ? `No transactions found for vendor: ${selectedVendor}`
                    : "No transactions available"}
                </div>
              )}
            </div>
          </div>
        </div>
        {deleteModal.isOpen && (
          <div className="delete-modal-overlay">
            <div className="delete-modal">
              <div className="delete-modal-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="54" height="54">
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path fill="#ff4757" d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-11.414L9.172 7.757 7.757 9.172 10.586 12l-2.829 2.828 1.415 1.415L12 13.414l2.828 2.829 1.415-1.415L13.414 12l2.829-2.828-1.415-1.415L12 10.586z" />
                </svg>
              </div>
              <h3>Confirm Delete</h3>
              <p>
                Are you sure you want to delete this Bill: <span className="trip-id-highlight">{deleteModal.billId}</span><br></br>
                <br></br>
              </p>
              {/* <p className="warning-text">This action cannot be undone.</p> */}
              <p><i>This action cannot be undone.</i></p><br></br>
              <div className="delete-modal-buttons">
                <button className="cancel-btn" onClick={() => setDeleteModal({ isOpen: false, recordId: null, billId: null })}>
                  Cancel
                </button>
                <button className="confirm-delete-btn" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  };

  export default Transactions;